import React, { useState, useEffect, useRef } from "react";
import { Box, Grid, Card, CardContent, Typography, Switch, FormControlLabel } from "@mui/material";
import fetchData from "verticales/FetchData";
import AppLoading from "verticales/AppLoading.tsx";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import * as echarts from "echarts";

const url = "https://5djo5qwa3l.execute-api.us-east-1.amazonaws.com/dimo-goyito/ec2/";
const context = "informes";

const cardDetails = [
  { title: "Enviados", action: "correos-enviados-claro-basic", type: "line" },
  { title: "Abiertos", action: "correos-abiertos-claro", type: "line" },
  { title: "Entregados", action: "correos-entregados-claro", type: "line" },
  // { title: "Fallidos", action: "correos-fallidos-claro", type: "line" },
  { title: "Click", action: "correos-click", type: "line" },
  { title: "Queja", action: "correos-complaint", type: "line" },
];

const ViewEventosClaro = () => {
  const [timeFilterTab, setTimeFilterTab] = useState("weekly"); // Default is "weekly"
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [totalEnvios, setTotalEnvios] = useState(0);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const chartRef = useRef(null);

  // Handle filter changes
  const handleTimeFilterChange = (event) => {
    const { name, checked } = event.target;
    if (checked) {
      setTimeFilterTab(name);
    }
  };

  const getTimeFilter = () => {
    switch (timeFilterTab) {
      case "monthly":
        return "monthly";
      case "weekly":
        return "weekly";
      case "custom":
        return "custom";
      default:
        return "weekly";
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const timeFilter = getTimeFilter();
      let total = 0;
      const newData = {};
      let currentTime;
      let startTime;
      let endTime;

      switch (timeFilter) {
        case "monthly":
          currentTime = Math.floor(new Date().getTime() / 1000) - 30 * 24 * 60 * 60;
          break;
        case "weekly":
          currentTime = Math.floor(new Date().getTime() / 1000) - 7 * 24 * 60 * 60;
          break;
        case "daily":
          currentTime = Math.floor(new Date().getTime() / 1000) - 24 * 60 * 60;
          break;
        case "custom":
          if (startDate && endDate) {
            startTime = dayjs(startDate).startOf("day").valueOf();
            endTime = dayjs(endDate).endOf("day").valueOf();
            if (startTime > endTime) {
              console.error("La fecha de inicio no puede ser mayor que la fecha de fin");
              return;
            }
          } else {
            console.error("Ambas fechas deben estar definidas y ser válidas");
            return;
          }
          break;
        default:
          currentTime = Math.floor(new Date().getTime() / 1000) - 7 * 24 * 60 * 60;
      }

      for (const card of cardDetails) {
        const requestPayload =
          timeFilter === "custom"
            ? { action: card.action, startTime, endTime }
            : { action: card.action, timeFilter: currentTime };

        const response = await fetchData(url, context, requestPayload);
        const parsedData = response.rows.map((item) => ({
          value: parseInt(item.total, 10),
          name: item.formatted_date,
        }));

        const totalPerEvent = parsedData.reduce((acc, item) => acc + item.value, 0);

        newData[card.title] = {
          total: totalPerEvent, // Suma total de valores
          data: parsedData, // Datos completos para el gráfico
        };

        total += totalPerEvent; // Acumular al total general
      }
      setData(newData);
      setTotalEnvios(total);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [timeFilterTab, startDate, endDate]);

  useEffect(() => {
    if (chartRef.current && data.Enviados) {
      const myChart = echarts.init(chartRef.current);

      const option = {
        title: {
          text: "Eventos de Correo",
        },
        tooltip: {
          trigger: "axis",
        },
        legend: {
          data: cardDetails.map((card) => card.title),
        },
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: data.Enviados.data.map((item) => item.name), // Usamos data.Enviados.data
        },
        yAxis: {
          type: "value",
        },
        series: cardDetails.map((card) => ({
          name: card.title,
          type: "line",
          stack: "Total",
          data: data[card.title]?.data.map((item) => item.value) || [], // Accedemos al array data
        })),
      };

      myChart.setOption(option);

      return () => {
        myChart.dispose(); // Limpiar el gráfico al desmontar el componente
      };
    }
  }, [data]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ flexGrow: 1, bgcolor: "background.paper" }}>
        {/* Time Filter Switches (Horizontal Layout) */}
        <Box sx={{ display: "flex", justifyContent: "space-evenly", marginBottom: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={timeFilterTab === "weekly"}
                onChange={handleTimeFilterChange}
                name="weekly"
              />
            }
            label="Semanal"
          />
          <FormControlLabel
            control={
              <Switch
                checked={timeFilterTab === "monthly"}
                onChange={handleTimeFilterChange}
                name="monthly"
              />
            }
            label="Mensual"
          />
          <FormControlLabel
            control={
              <Switch
                checked={timeFilterTab === "custom"}
                onChange={handleTimeFilterChange}
                name="custom"
              />
            }
            label="Personalizado"
          />
        </Box>

        {/* Date Pickers for Custom Date Range */}
        {timeFilterTab === "custom" && (
          <Box display="flex" justifyContent="center" mb={2}>
            <DatePicker
              label="Fecha de inicio"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
            />
            <DatePicker
              label="Fecha de fin"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
            />
          </Box>
        )}

        {/* Data Loading Indicator */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
              position: "absolute",
              top: 0,
              left: 0,
            }}
          >
            <AppLoading titleCustom={"Envios Claro"} />
          </Box>
        ) : (
          <Grid container spacing={2} justifyContent="flex-start" sx={{ marginBottom: 6 }}>
            <Grid item xs={12} sm={2} md={2}>
              <Card>
                <CardContent>
                  <Typography variant="h6">Total de Envíos</Typography>
                  <Typography variant="h4" color="secondary">
                    {totalEnvios}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {/* Display each card in the same row */}
            {cardDetails.map((card) => (
              <Grid item xs={12} sm={2} md={2} key={card.title}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">{card.title}</Typography>
                    <Typography variant="h4" color="primary">
                      {data[card.title]?.total || 0} {/* Mostrar el total acumulado */}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* ECharts Graph */}
        <Box sx={{ width: "100%", height: 400 }} ref={chartRef}></Box>
      </Box>
    </LocalizationProvider>
  );
};

export default ViewEventosClaro;
